import Orders from '../schema/injected/Orders';
import Shipments from '../schema/injected/Shipments';

import shoppingCartApi from '../../api/shoppingcarts';
import ordersApi from '../../api/orders';
import { SHIPMENTS as shipmentsTypes } from '../mutation-types';

import { SHIPMENT_TYPE } from '../../constants/shop';
import { SHIPPING_METHODS } from '../../../../../../lib/document/schema/shipping_method';

import pick from 'latest_modules/lodash/pick';
import find from 'latest_modules/lodash/find';

// Getters
const getters = Object.assign(Shipments.getters, {
  findOneByDigitalContentGiftId() {
    return function (id) {
      return Shipments.findOne(function (shipment) {
        return find(shipment.items, function (item) {
          return item.gift && item.gift._id === id;
        });
      });
    };
  }
});

//Actions
const actions = {
  insert({ dispatch, commit }, payload) {
    const shipmentData = pick(payload, [
      '_id',
      'state',
      'user',
      'wishlist_user',
      'average_delivery_time',
      'product_total',
      'shipment_total',
      'shipping_charge',
      'import_fees',
      'gift_wrapping',
      'gift_message',
      'gift_wrapping_total_charge',
      'can_cancel',
      'has_pre_order_sku',
      'has_digital_content',
      'has_reviewed',
      'canceled_at',
      'region',
      'shipment_type',
      'shipping_method',
      'shipping_charges',
      'additional_charges',
      'undeliverable'
    ]);

    if (payload.shipmentgroup) {
      shipmentData.shipmentgroup = payload.shipmentgroup._id;
    }

    shipmentData.items = [];
    payload.items.forEach(function (item) {
      shipmentData.items.push({
        product: item.product,
        has_reviewed: item.has_reviewed,
        sku: pick(item.sku, [
          '_id',
          'no',
          'titles',
          'short_titles',
          'image',
          'price',
          'prices',
          'purchase_price',
          'available_price',
          'sales_method',
          'has_digital_content',
          'third_party_asset_name',
          'digital_content',
          'max_purchase_qty',
          'expected_ship_at',
          'format_expected_ship_at_or_extra_week',
          'has_premium_price',
          'region',
          'flags',
          'undeliverable',
          'serial_codes',
          'has_serial_codes'
        ]),
        qty: item.qty,
        gift: item.gift,
        is_divided: item.is_divided,
        labels: item.labels,
        suppliers: item.suppliers,
        children: item.children,
        is_split_from_children: item.is_split_from_children,
        is_split_from_children_and_new: item.is_split_from_children_and_new,
        variation: item.variation
      });
    });

    if (payload.shipmentgroup) {
      dispatch('shipmentGroups/insert', payload.shipmentgroup, {
        root: true
      });
    }

    commit(shipmentsTypes.UPSERT, shipmentData);
  },

  // Shipment の注文をキャンセルする
  requestCancellation({ dispatch, commit }, shipment) {
    const order = Orders.findOne(function (o) {
      return o.shipments.indexOf(shipment._id) >= 0;
    });
    if (!order) {
      return Promise.reject(new Error('Order data was not found.'));
    }
    return new Promise(function (resolve, reject) {
      ordersApi
        .cancelShipment({
          order_id: order._id,
          shipment_id: shipment._id,
          shipmentgroup_id: shipment.shipmentgroup
        })
        .then(function () {
          commit(shipmentsTypes.REQUEST_CANCEL, {
            _id: shipment._id
          });

          // ShipmentGroups にも伝搬させる
          dispatch(
            'shipmentGroups/requestCancellation',
            {
              _id: shipment.shipmentgroup._id || shipment.shipmentgroup
            },
            {
              root: true
            }
          );

          resolve();
        })
        .catch(function (err) {
          reject(err);
        });
    });
  },

  // Digital Content Gift (TOM Premium Gift) のメールを送付者に再送信する
  resendDigitalContentGiftEmail({ commit }, shipment) {
    const giftIndex = shipment.items.findIndex(function (item) {
      return item.gift !== undefined;
    });
    if (giftIndex < 0) {
      return Promise.reject();
    }
    return new Promise(function (resolve, reject) {
      ordersApi
        .resendDigitalContentGiftEmail({
          id: shipment.items[giftIndex].gift._id
        })
        .then(function (res) {
          if (res.sent_at) {
            commit(shipmentsTypes.UPDATE_DIGITAL_CONTENT_GIFT_SENT_AT, {
              _id: shipment._id,
              item_index: giftIndex,
              sent_at: res.sent_at
            });
            resolve(res.data);
          }
        })
        .catch((err) => {
          if (err.responseText) {
            const error = JSON.parse(err.responseText);
            reject(error);
          }
        });
    });
  },

  clear({ commit }) {
    return new Promise(function (resolve) {
      commit(shipmentsTypes.CLEAR);
      resolve();
    });
  },

  changeQty({ commit }, payload) {
    commit(shipmentsTypes.CHANGE_QTY, {
      _id: payload._id,
      shipment_id: payload.shipment_id,
      item_id: payload.item_id,
      qty: payload.qty
    });
  },

  updateGiftWrapping({ dispatch, commit }, payload) {
    return new Promise(function (resolve, reject) {
      shoppingCartApi.updateGiftWrapping(
        {
          cartId: payload.cartId,
          shipmentId: payload.shipmentId,
          giftWrapping: payload.giftWrapping,
          giftMessage: payload.giftMessage,
          wishlistId: payload.wishlistId
        },
        function (err, res) {
          if (err) return reject(err);
          commit(shipmentsTypes.UPDATE_GIFT_WRAPPING, {
            _id: payload.shipmentId,
            gift_wrapping: res.gift_wrapping,
            gift_message: res.gift_message
          });

          dispatch(
            'cart/updatePrice',
            {
              cart: res.prices
            },
            {
              root: true
            }
          );

          // 有効期限を更新
          dispatch(
            'cart/updateCountdownToExpiration',
            {
              expiration_seconds: res.expiration_seconds
            },
            {
              root: true
            }
          );

          resolve(res);
        }
      );
    });
  },
  update({ dispatch }, payload = {}) {
    return new Promise(function (resolve) {
      payload.shipments.forEach((shipment) => {
        // has_digital_contentを設定
        shipment.has_digital_content =
          shipment.shipment_type === SHIPMENT_TYPE.DIGITAL_PRODUCT;

        // shipping_charges.average_delivery_timeを設定
        // shipping_charges.recommendsを設定
        if (payload.shippingChargesMap) {
          const shippingCharges = payload.shippingChargesMap[shipment._id];

          shipment.shipping_charges = shippingCharges.map((shippingCharge) => {
            const shippingMethod = find(SHIPPING_METHODS, {
              value: shippingCharge.shipping_method
            });

            shippingCharge.average_delivery_time =
              shippingMethod.average_delivery_time;
            shippingCharge.recommends = shippingMethod.recommends;

            return shippingCharge;
          });
        }

        // shipping_charges.selectedを設定
        if (
          payload.shippingMethod &&
          payload.shippingMethod.shipmentId === shipment._id
        ) {
          shipment.shipping_charges = shipment.shipping_charges.map(
            (shippingCharge) => {
              shippingCharge.selected =
                shippingCharge.shipping_method === payload.shippingMethod.value;

              return shippingCharge;
            }
          );
        }

        // shipment.import_feesを設定
        const selectedShippingCharge = shipment.shipping_charges.find(
          (c) => c.selected
        );
        if (selectedShippingCharge) {
          shipment.import_fees = selectedShippingCharge.import_fees;
        }

        // shipmentとshipment.item.skuのundeliverableを設定
        if (payload.shipmentMap) {
          shipment.undeliverable =
            payload.shipmentMap[shipment._id].undeliverable;

          shipment.items.forEach((item) => {
            if (payload.shipmentMap[shipment._id].items[item.sku._id]) {
              item.sku.undeliverable =
                payload.shipmentMap[shipment._id].items[
                  item.sku._id
                ].undeliverable;
            }
          });
        }

        dispatch('insert', shipment);
      });

      resolve();
    });
  }
};

// Mutations
const mutations = {
  [shipmentsTypes.UPSERT](state, payload) {
    Shipments.update(
      {
        _id: payload._id
      },
      payload,
      {
        upsert: true
      }
    );
  },

  [shipmentsTypes.REQUEST_CANCEL](state, payload) {
    Shipments.updateById(payload._id, {
      state: Shipments.STATE.CANCEL_REQUESTED,
      canceled_at: new Date()
    });
  },

  [shipmentsTypes.UPDATE_DIGITAL_CONTENT_GIFT_SENT_AT](state, payload) {
    const shipment = Shipments.findById(payload._id);
    shipment.items[payload.item_index].gift.sent_at = payload.sent_at;
  },

  [shipmentsTypes.CLEAR]() {
    Shipments.remove();
  },

  [shipmentsTypes.CHANGE_QTY](state, payload) {
    state.all[payload.shipment_id].items.forEach((item) => {
      if (item.sku._id === payload.item_id) {
        item.qty = payload.qty;
      }
    });
  },

  [shipmentsTypes.UPDATE_GIFT_WRAPPING](state, payload) {
    Shipments.updateById(payload._id, {
      gift_wrapping: payload.gift_wrapping,
      gift_message: payload.gift_message
    });
  }
};

export default {
  namespaced: true,
  state: Shipments.state,
  getters,
  actions,
  mutations
};
